// AI-CONTEXT: See docs/features/subscription-notes-column.md
import { useState } from 'react'
import firebase from '../../firebase/compat'
import TableCell from '../common/table/TableCell.jsx'
import Tooltip from '../common/Tooltip'
import { plainTexts, TOOLTIP_WIDTH } from '../../helpers/notes/notePreview'

/**
 * De interne notitie bij één organisatie in het abonnementenoverzicht.
 *
 * Twee standen: de tekst afgekapt in de rij, en na een klik een tekstvak. Niet
 * permanent een tekstvak zoals bij het bedrag — 98 tekstvakken maken de tabel
 * breed en een lange notitie is dan niet in één oogopslag te lezen.
 *
 * De tekst leeft in deze component en wordt NIET opnieuw uit de props gevuld.
 * De pagina vervangt na elke opslag één rij in haar lijst; zou dat dit veld
 * terugzetten, dan verdwijnt de notitie die je in een andere rij aan het typen
 * bent. Bij ~98 organisaties werk je er meerdere tegelijk af.
 * AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
 *
 * De tooltip is muis-only, maar deze pagina bestaat alleen op de
 * Deccos-beheertenant en heeft geen mobiele route — en de volledige tekst is
 * altijd ook via het tekstvak te bereiken, dus hij is nooit de enige weg.
 * AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile
 *
 * @param {object} props
 * @param {{compagnyId: string, note: string}} props.row - rij uit getSubscriptionsOverview
 * @param {(row: object) => void} props.onSaved - krijgt de bijgewerkte rij na een geslaagde opslag
 */
const SubscriptionNoteCell = ({ row, onSaved }) => {
    // Beginwaarde uit de props, daarna van de gebruiker. Bewust géén useEffect
    // die meeloopt met `row` — zie de toelichting hierboven. `saved` is wat er
    // volgens de server staat, zodat annuleren daarheen terug kan.
    const [saved, setSaved] = useState(row.note || '')
    const [text, setText] = useState(row.note || '')
    const [editing, setEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(/** @type {string | null} */ (null))

    const handleSave = async () => {
        setError(null)
        setSaving(true)
        try {
            // v2-functie in europe-west1 — region expliciet, anders belt de SDK
            // us-central1 en bestaat de functie daar niet.
            const setSubscriptionNote = firebase.app().functions('europe-west1')
                .httpsCallable('setSubscriptionNote')
            const result = await setSubscriptionNote({ compagnyId: row.compagnyId, note: text })

            // De server geeft de getrimde tekst terug; die tonen we, niet wat
            // er getypt werd — anders staat er iets anders op het scherm dan in
            // de database.
            const opgeslagen = result.data?.note ?? ''
            setSaved(opgeslagen)
            setText(opgeslagen)
            setEditing(false)
            onSaved({ ...row, note: opgeslagen })
        } catch (err) {
            console.error('setSubscriptionNote failed:', err)
            // Het tekstvak blijft open met de getypte tekst: anders is het werk
            // weg en weet de gebruiker niet wat hij opnieuw moet typen.
            setError(err.message || 'Kon de notitie niet opslaan.')
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        setText(saved)
        setError(null)
        setEditing(false)
    }

    if (editing) {
        return (
            <TableCell>
                <textarea
                    aria-label='Notitie'
                    className='subscriptions-note-input'
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                />
                <div className='subscriptions-note-actions'>
                    <button type='button' onClick={handleSave} disabled={saving}>
                        {saving ? '…' : 'Opslaan'}
                    </button>
                    <button type='button' className='subscriptions-note-cancel' onClick={handleCancel}>
                        Annuleren
                    </button>
                </div>
                {error && <span className='subscriptions-note-error'>{error}</span>}
            </TableCell>
        )
    }

    // plainTexts, niet noteTexts: die haalt er eerst de HTML uit, en dat is op
    // platte tekst niet neutraal maar schadelijk — 'budget <500> per maand'
    // verliest dan het stuk tussen de haken, terwijl het bewerkveld de echte
    // tekst toont. SubscriptionNotes.Note is platte tekst.
    // AI-CONTEXT: See docs/pitfalls.md#dev-helper-lossy-on-input-it-claims-to-accept
    const { preview, full } = plainTexts(saved)

    const knop = (
        // aria-label vast, zodat de knop een naam houdt die niet met de inhoud
        // meebeweegt. Bewust geen <p> erin: `.tooltip-container p` staat
        // globaal op wit en zou de tekst op een lichte rij onzichtbaar maken
        // (inbox #137).
        <button
            type='button'
            aria-label='Notitie bewerken'
            className={preview ? 'subscriptions-note-open' : 'subscriptions-note-open subscriptions-note-open--empty'}
            onClick={() => setEditing(true)}
        >
            {preview || '+ Notitie'}
        </button>
    )

    return (
        <TableCell>
            {/* Geen tooltip als de hele tekst al in de cel staat: hoveren toont
                dan een leeg blokje. Zelfde regel als NoteBodyCell. */}
            {full && full !== preview
                ? <Tooltip content={full} maxWidth={TOOLTIP_WIDTH}>{knop}</Tooltip>
                : knop}
        </TableCell>
    )
}

export default SubscriptionNoteCell
